import { EmptyState } from "../../app/Status";
import { cloneElement, Fragment, useId, useMemo, useState, type ReactElement } from "react";
import {
  ACTIVE_STATUSES,
  NLSFB,
  nlsfbLabel,
  optLabel,
  PRIO,
  PRIORITIES,
  Q,
  STATUS,
  statusLabel,
  TYPES,
  typeLabel,
  type BuildingDto,
  type IssueSummary,
} from "@klantpunt/shared";
import { downloadCsv, issuesCsv } from "./csv";
import { activeFilterCount, isFiltered, locText, type Filters } from "./filters";
import { isLocal } from "../../offline/ops";
import { IconChevronDown, IconChevronUp, IconDownload, IconFilter, IconIssues } from "../../app/icons";
import { Modal } from "../../app/Modal";
import { PHONE_QUERY, useMediaQuery } from "../../lib/useMediaQuery";
import { AssetBadge } from "../assets/assetIcons";

export function FilterBar({ f, setF, withScope }: { f: Filters; setF: (f: Filters) => void; withScope: boolean }) {
  return (
    <div className="filters">
      <select aria-label="Status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
        <option value="actief">Actief</option>
        <option value="alle">Alle statussen</option>
        {STATUS.map((s) => (
          <option key={s.k} value={s.k}>
            {s.l}
          </option>
        ))}
      </select>
      <select aria-label="Prioriteit" value={f.prio} onChange={(e) => setF({ ...f, prio: e.target.value })}>
        <option value="">Alle prioriteiten</option>
        {PRIORITIES.map((p) => (
          <option key={p} value={p}>
            {p} {PRIO[p].l}
          </option>
        ))}
      </select>
      <select aria-label="Type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
        <option value="">Alle types</option>
        {TYPES.map((t) => (
          <option key={t.k} value={t.k}>
            {t.l}
          </option>
        ))}
      </select>
      <select aria-label="Herkomst" value={f.origin} onChange={(e) => setF({ ...f, origin: e.target.value })}>
        <option value="">Alle herkomsten</option>
        {Q.origin.opts.map((o) => (
          <option key={o.k} value={o.k}>
            {o.l}
          </option>
        ))}
      </select>
      <select aria-label="Element" value={f.element} onChange={(e) => setF({ ...f, element: e.target.value })}>
        <option value="">Alle elementen</option>
        <option value="none">Zonder element</option>
        {NLSFB.map((m) => (
          <optgroup key={m.code} label={`${m.code}- ${m.label}`}>
            <option value={m.code}>
              {m.code}- {m.label} (alle)
            </option>
            {m.groups.map((g) => (
              <option key={g.code} value={g.code}>
                {g.code} {g.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {withScope && (
        <select
          aria-label="Bereik"
          value={f.scope}
          onChange={(e) => setF({ ...f, scope: e.target.value as Filters["scope"] })}
        >
          <option value="floor">Deze verdieping</option>
          <option value="all">Heel gebouw</option>
        </select>
      )}
    </div>
  );
}

const resetFilters = (f: Filters): Filters => ({
  ...f,
  q: "",
  prio: "",
  type: "",
  origin: "",
  element: "",
  status: "actief",
});

function IssueRow({
  i,
  b,
  selected,
  withFloor,
  onOpen,
}: {
  i: IssueSummary;
  b: BuildingDto;
  selected: boolean;
  withFloor: boolean;
  onOpen: () => void;
}) {
  const active = ACTIVE_STATUSES.has(i.status);
  return (
    <button type="button" className="irow" aria-current={selected} onClick={onOpen}>
      <span className={`stripe ${active ? `prio-${i.priority}` : "inactive"}`} />
      <span className="code small">{i.code}</span>
      <span className="r">
        <span className={`chip prio prio-${i.priority}`}>{i.priority}</span>
      </span>
      <span className="t">{i.title}</span>
      <span className="l">{locText(i, b, withFloor)}</span>
      <span className="tags">
        {isLocal(i) && <span className="chip pending">Wacht op verbinding</span>}
        <span className={`chip st-${i.status}`}>{statusLabel(i.status)}</span>
        <span className="chip">{typeLabel(i.answers.type)}</span>
        {i.answers.origin !== "ronde" && <span className="chip">{optLabel("origin", i.answers.origin)}</span>}
        {i.taskCode && <span className="chip">{i.taskCode}</span>}
        {i.commentCount > 0 && <span className="chip">{i.commentCount} opm.</span>}
      </span>
    </button>
  );
}

/** Lijst naast de tekening (deze verdieping / heel gebouw). */
export function IssueListPane({
  building: b,
  list,
  floorName,
  f,
  setF,
  selected,
  onOpen,
  children,
  withScope = true,
}: {
  building: BuildingDto;
  list: IssueSummary[];
  floorName: string | null;
  /** Keuze "Deze verdieping / Heel gebouw"; niet als de plek al boven de lijst staat. */
  withScope?: boolean;
  f: Filters;
  setF: (f: Filters) => void;
  selected: string | null;
  onOpen: (id: string) => void;
  children?: React.ReactNode;
}) {
  const phone = useMediaQuery(PHONE_QUERY);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersId = useId();
  const count = activeFilterCount(f);
  const filterControls = (
    <>
      <FilterBar f={f} setF={setF} withScope={withScope} />
      {isFiltered(f) && (
        <button type="button" className="btn small" onClick={() => setF(resetFilters(f))}>
          Filters wissen
        </button>
      )}
    </>
  );
  return (
    <section className="listpane" aria-label="Bevindingen">
      <div className="lhead">
        <div className="row">
          <h2>{f.scope === "floor" && floorName ? floorName : "Heel gebouw"}</h2>
          <span className="muted num" data-testid="list-count">
            {list.length}
          </span>
        </div>
      </div>
      {/* Zoeken blijft altijd zichtbaar (op de telefoon sticky); de keuzelijsten klappen in. */}
      <div className="lsearch">
        <input
          type="search"
          placeholder="Zoek op ID, titel, ruimte…"
          aria-label="Zoeken"
          value={f.q}
          onChange={(e) => setF({ ...f, q: e.target.value })}
        />
        <button
          type="button"
          className="btn small"
          aria-expanded={filtersOpen}
          aria-controls={filtersOpen ? filtersId : undefined}
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          <IconFilter size={18} aria-hidden="true" />
          {count ? `Filters (${count})` : "Filters"}
        </button>
      </div>
      {filtersOpen &&
        (phone ? (
          <Modal id={filtersId} title="Filters" onClose={() => setFiltersOpen(false)}>
            <div className="filtersheet">{filterControls}</div>
          </Modal>
        ) : (
          <div className="lfilters" id={filtersId}>
            {filterControls}
          </div>
        ))}
      <div className="ilist">
        {list.map((i) => (
          <IssueRow
            key={i.id}
            i={i}
            b={b}
            selected={i.id === selected}
            withFloor={f.scope !== "floor"}
            onOpen={() => onOpen(i.id)}
          />
        ))}
        {list.length === 0 && (
          <EmptyState icon={IconIssues} title="Geen bevindingen" text="Niets gevonden met deze filters." />
        )}
      </div>
      {children}
    </section>
  );
}

type SortKey =
  "code" | "priority" | "title" | "floor" | "room" | "type" | "element" | "origin" | "status" | "task" | "updated";
/** Kolommen in volgorde van belang: wat je zoekt (status) staat vooraan, ook als de tabel moet schuiven. */
const COLS: [SortKey, string][] = [
  ["code", "ID"],
  ["priority", "Prio"],
  ["title", "Bevinding"],
  ["status", "Status"],
  ["room", "Ruimte / asset"],
  ["floor", "Verdieping"],
  ["type", "Type"],
  ["element", "Element"],
  ["origin", "Herkomst"],
  ["task", "Taak"],
  ["updated", "Gewijzigd"],
];

/** Tabelweergave met sorteerbare kolommen en CSV-export. */
export function IssueTable({
  building: b,
  list,
  floorName,
  f,
  setF,
  selected,
  onOpen,
}: {
  building: BuildingDto;
  list: IssueSummary[];
  /** Huidige verdieping; bij bereik "Deze verdieping" staat de lijst daarop gefilterd. */
  floorName?: string;
  f: Filters;
  setF: (f: Filters) => void;
  selected: string | null;
  onOpen: (id: string) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null);
  const floorOf = (i: IssueSummary) => b.floors.find((x) => x.id === i.locations[0]?.floorId);
  const roomInst = (i: IssueSummary) =>
    [b.rooms.find((r) => r.id === i.locations[0]?.roomId)?.name, b.assets.find((x) => x.id === i.installationId)?.name]
      .filter(Boolean)
      .join(" · ");

  const rows = useMemo(() => {
    if (!sort) return list;
    const val = (i: IssueSummary): string | number => {
      switch (sort.key) {
        case "code":
          return i.seq;
        case "priority":
          return i.priority;
        case "title":
          return i.title.toLowerCase();
        case "floor":
          return floorOf(i)?.sort ?? 9999;
        case "room":
          return roomInst(i).toLowerCase();
        case "type":
          return typeLabel(i.answers.type);
        case "element":
          return i.elementCode ?? "~";
        case "origin":
          return optLabel("origin", i.answers.origin);
        case "status":
          return STATUS.findIndex((s) => s.k === i.status);
        case "task":
          return i.taskCode ?? "~";
        case "updated":
          return i.updatedAt;
      }
    };
    return [...list].sort((a, c) => {
      const x = val(a);
      const y = val(c);
      return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, sort]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersId = useId();
  const count = activeFilterCount(f);
  // Kolommen die in deze lijst niets zeggen vallen weg: één verdieping gekozen, of nergens een element.
  const cols = COLS.filter(
    ([key]) => !(key === "floor" && floorName) && !(key === "element" && !list.some((i) => i.elementCode)),
  );
  const cell = (key: SortKey, i: IssueSummary) => {
    switch (key) {
      case "code":
        return <td className="code">{i.code}</td>;
      case "priority":
        return (
          <td>
            <span className={`chip prio prio-${i.priority}`}>{i.priority}</span>
          </td>
        );
      case "title":
        return <td className="tcell-title">{i.title}</td>;
      case "status":
        return (
          <td>
            <span className={`chip st-${i.status}`}>{statusLabel(i.status)}</span>
          </td>
        );
      case "room":
        return (
          <td className="tcell-room" title={roomInst(i) || undefined}>
            {roomInst(i) || "—"}
            {i.locations.length > 1 && <span className="chip">+{i.locations.length - 1}</span>}
          </td>
        );
      case "floor":
        return <td>{floorOf(i)?.name ?? "—"}</td>;
      case "type":
        return <td>{typeLabel(i.answers.type)}</td>;
      case "element":
        return (
          <td title={i.elementCode ? nlsfbLabel(i.elementCode) : undefined}>
            {i.elementCode ? (
              <span className="elcell">
                <AssetBadge code={i.elementCode} size={20} />
                <span className="num">{i.elementCode}</span>
              </span>
            ) : (
              "—"
            )}
          </td>
        );
      case "origin":
        return <td>{optLabel("origin", i.answers.origin)}</td>;
      case "task":
        return <td>{i.taskCode ? <span className="code">{i.taskCode}</span> : "—"}</td>;
      case "updated":
        return (
          <td className="num muted">
            {new Date(i.updatedAt).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}
          </td>
        );
    }
  };

  return (
    <section className="tablewrap issuetable" aria-label="Alle bevindingen">
      <div className="tablehead">
        <div className="grow">
          <p className="label">
            {b.name} · {floorName ?? "Heel gebouw"}
          </p>
          <h1>
            Bevindingen <span className="muted num">{list.length}</span>
          </h1>
        </div>
        <span className="pageactions">
          <button
            type="button"
            className="btn ghost"
            title="Deze lijst downloaden als CSV (Excel)"
            onClick={() =>
              downloadCsv(issuesCsv(rows, b), `bevindingen-${b.code}-${new Date().toISOString().slice(0, 10)}.csv`)
            }
          >
            <IconDownload size={18} aria-hidden="true" />
            CSV
          </button>
        </span>
      </div>
      <div className="tablefilters">
        <div className="lsearch">
          <input
            type="search"
            placeholder="Zoek op ID, titel, ruimte…"
            aria-label="Zoeken"
            value={f.q}
            onChange={(e) => setF({ ...f, q: e.target.value })}
          />
          <button
            type="button"
            className="btn"
            aria-expanded={filtersOpen}
            aria-controls={filtersOpen ? filtersId : undefined}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <IconFilter size={18} aria-hidden="true" />
            {count ? `Filters (${count})` : "Filters"}
          </button>
        </div>
        {filtersOpen && (
          <div className="lfilters" id={filtersId}>
            <FilterBar f={f} setF={setF} withScope={false} />
          </div>
        )}
        {isFiltered(f) && !filtersOpen && (
          <div className="row">
            <span className="small muted">{count === 1 ? "1 filter actief" : `${count} filters actief`}</span>
            <button type="button" className="linkbtn" onClick={() => setF(resetFilters(f))}>
              Filters wissen
            </button>
          </div>
        )}
        {isFiltered(f) && filtersOpen && (
          <button type="button" className="btn small" onClick={() => setF(resetFilters(f))}>
            Filters wissen
          </button>
        )}
      </div>
      <div className="tscroll">
        <table className="it">
          <thead>
            <tr>
              {cols.map(([key, label]) => (
                <th
                  key={key}
                  data-col={key}
                  aria-sort={sort && key === sort.key ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
                >
                  <button
                    type="button"
                    className="thbtn"
                    onClick={() =>
                      setSort((s) => (s?.key === key ? (s.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }))
                    }
                  >
                    {label}
                    {sort?.key === key &&
                      (sort.dir === 1 ? (
                        <IconChevronUp size={14} aria-hidden="true" />
                      ) : (
                        <IconChevronDown size={14} aria-hidden="true" />
                      ))}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr
                key={i.id}
                aria-current={i.id === selected}
                tabIndex={0}
                onClick={() => onOpen(i.id)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen(i.id))}
              >
                {cols.map(([key]) => (
                  <Fragment key={key}>
                    {cloneElement(cell(key, i) as ReactElement<{ "data-col"?: string }>, { "data-col": key })}
                  </Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <EmptyState icon={IconIssues} title="Geen bevindingen" text="Niets gevonden met deze filters." />
        )}
      </div>
    </section>
  );
}
